import type * as THREE from 'three'
import {
  cloneGlbHierarchyUserDataMeta,
  type ImportedBodyEntry,
  type GlbHierarchyNode,
  type GlbHierarchyNodeMeta,
  type GlbHierarchyRow,
} from '../../imported-model/loaders/glb'
import {
  createFallbackImportedBodyEntry,
  isRuntimeImportedBodyEntryLabel,
  mergeBodyEntriesById,
  normalizeImportedBodyEntryDisplay,
  parseImportedBodyEntries,
} from '../../imported-model/loaders/body-entries'
import type { FileInfo } from '../../imported-model/loaders/file-info'
import type { SelectionTarget } from '../../imported-model/loaders/types'
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata'
import {
  createImportedBodyOwnershipLookupKeys,
  resolveStableImportedBodyBindingId,
} from '../../imported-model/utils'
import { resolveBodyHighlightTargetsById } from '../../imported-model/loaders/assembly-node-utils'
import {
  AssemblyComponentFixationService,
  AssemblyComponentFixationState,
} from '../../assembly/component-fixation'
import type { MateEntityRef } from '../../assembly/definition'
import {
  AssemblyReferenceCopyLineageMetadataService,
  AssemblyReferenceCopyLineageRole,
  type AssemblyReferenceCopyLineageMetadata,
} from '../../assembly/reference-copy'
import type {
  PuppyAssemblyDocument,
  PuppyAssemblyNode,
} from './types'
import { PuppyAssemblyComponentRootMaterializer } from './PuppyAssemblyComponentRootMaterializer'

/** bodyBindingId 到 Mate 持久化组件选择目标候选的索引。 */
type BodyBindingSelectionTargetIndex = Record<string, SelectionTarget[]>

/**
 * bodyBindingId 到 assembly node 直接拥有者的索引。
 *
 * 设计说明：
 * display.glb 的可见 mesh 可能因为导出快照或引用复制而继承了上层装配的 assemblyNodeId，
 * 但它自身携带的 bodyBindingId 仍然是几何归属的精确语义。该索引用于在构建 imported metadata
 * 前先恢复对象的真实零件边界，避免 Move/Rotate 把命中的 body 写回装配根。
 */
interface BodyBindingAssemblyNodeOwnershipIndex {
  /** 可唯一命中的 body 归属 key 到 assembly node ID 的映射。*/
  readonly nodeIdByLookupKey: Map<string, string>
  /** 被多个 assembly node 共享的 body 归属 key；命中这些 key 时不能用于改写归属。*/
  readonly ambiguousLookupKeys: Set<string>
}

/**
 * `.puppy` 装配层级恢复结果。
 */
export interface PuppyAssemblyHierarchyBuildResult {
  /** 设计树根节点。*/
  hierarchy: GlbHierarchyNode
  /** 扁平层级行。*/
  rows: GlbHierarchyRow[]
  /** assembly node ID 到运行时 Object3D UUID 的索引。*/
  objectIdByAssemblyNodeId: Record<string, string>
  /** assembly node ID 到选择代理 Object3D UUID 的索引。*/
  selectionObjectIdByAssemblyNodeId: Record<string, string>
}

/**
 * `.puppy` 装配节点运行时绑定。
 */
interface PuppyAssemblyRuntimeBinding {
  /** 当前 assembly node 对应的 display.glb 运行时对象。 */
  object: THREE.Object3D
  /** 当前节点命中后应回退到的选择代理对象。 */
  selectionObject: THREE.Object3D
  /** 当前节点命中后应回退到的选择代理对象 ID。 */
  selectionObjectId: string
}

/**
 * 读取 Object3D 上的 puppy 扩展记录。
 *
 * @param object 场景对象。
 * @returns puppy 扩展记录。
 */
function readPuppyUserData(object: THREE.Object3D): Record<string, unknown> | undefined {
  const puppy = object.userData?.puppy
  return puppy && typeof puppy === 'object'
    ? puppy as Record<string, unknown>
    : undefined
}

/**
 * 读取离线包装配节点 ID。
 *
 * @param object 场景对象。
 * @returns 装配节点 ID。
 */
function readAssemblyNodeId(object: THREE.Object3D): string | undefined {
  const value = readPuppyUserData(object)?.assemblyNodeId
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

/**
 * 判断对象是否为装配语义节点本体。
 *
 * @param object 场景对象。
 * @returns 是否为语义节点本体。
 */
function isAssemblyNodeHost(object: THREE.Object3D): boolean {
  return readPuppyUserData(object)?.assemblyNodeRole === 'node'
}

/**
 * 按节点别名解析归一化后的 assembly node ID。
 *
 * @param nodeId 原始 assembly node ID。
 * @param nodeIdAliasByNodeId 被折叠节点到保留节点的映射。
 * @returns 归一化后的 assembly node ID。
 */
function resolveNormalizedAssemblyNodeId(
  nodeId: string,
  nodeIdAliasByNodeId: Readonly<Record<string, string>>,
): string {
  let current = nodeId
  const visited = new Set<string>()
  while (nodeIdAliasByNodeId[current] && !visited.has(current)) {
    visited.add(current)
    current = nodeIdAliasByNodeId[current]!
  }
  return current
}

/**
 * 向 body 归属索引写入一个候选 key。
 *
 * @param index body 归属索引。
 * @param lookupKey body 归属 key。
 * @param nodeId 当前拥有者 assembly node ID。
 */
function appendBodyBindingAssemblyNodeOwner(
  index: BodyBindingAssemblyNodeOwnershipIndex,
  lookupKey: string,
  nodeId: string,
): void {
  if (!lookupKey || index.ambiguousLookupKeys.has(lookupKey)) {
    return
  }

  const currentNodeId = index.nodeIdByLookupKey.get(lookupKey)
  if (!currentNodeId) {
    index.nodeIdByLookupKey.set(lookupKey, nodeId)
    return
  }
  if (currentNodeId === nodeId) {
    return
  }

  index.nodeIdByLookupKey.delete(lookupKey)
  index.ambiguousLookupKeys.add(lookupKey)
}

/**
 * 把 bodyEntries 中保留的实体身份写入 body 归属索引。
 *
 * 设计说明：历史包或被根级 display 污染过的包可能没有把 bodyBindingIds 写到 part 节点数组，
 * 但 part 的 meta.bodyEntries 仍保留了精确 bodyBindingId。导入端必须把它视为同等权威的
 * 零件归属来源，才能把可见 display mesh 从根节点恢复到真实零件边界。
 *
 * @param index body 归属索引。
 * @param entry 实体条目。
 * @param nodeId 当前拥有者 assembly node ID。
 */
function appendBodyEntryAssemblyNodeOwner(
  index: BodyBindingAssemblyNodeOwnershipIndex,
  entry: ImportedBodyEntry,
  nodeId: string,
): void {
  [
    entry.bodyBindingId,
    entry.bodyId,
    entry.label,
  ].forEach((bodyKey) => {
    createImportedBodyOwnershipLookupKeys(bodyKey).forEach((lookupKey) => {
      appendBodyBindingAssemblyNodeOwner(index, lookupKey, nodeId)
    })
  })
}

/**
 * 从 assembly.json 建立 bodyBindingId 到零件 assembly node 的归属索引。
 *
 * @param assembly `.puppy` 装配结构文档。
 * @param nodeIdAliasByNodeId 被折叠节点到保留节点的映射。
 * @returns bodyBinding 归属索引。
 */
function createBodyBindingAssemblyNodeOwnershipIndex(
  assembly: PuppyAssemblyDocument,
  nodeIdAliasByNodeId: Readonly<Record<string, string>>,
): BodyBindingAssemblyNodeOwnershipIndex {
  const index: BodyBindingAssemblyNodeOwnershipIndex = {
    nodeIdByLookupKey: new Map<string, string>(),
    ambiguousLookupKeys: new Set<string>(),
  }

  assembly.nodes.forEach((node) => {
    if (!canRestoreBodyEntriesForAssemblyNode(node)) {
      return
    }
    const ownerNodeId = resolveNormalizedAssemblyNodeId(node.nodeId, nodeIdAliasByNodeId)
    node.bodyBindingIds.forEach((bodyBindingId) => {
      createImportedBodyOwnershipLookupKeys(bodyBindingId).forEach((lookupKey) => {
        appendBodyBindingAssemblyNodeOwner(index, lookupKey, ownerNodeId)
      })
    })
    node.meta?.bodyEntries?.forEach((entry) => {
      appendBodyEntryAssemblyNodeOwner(index, entry, ownerNodeId)
    })
  })

  return index
}

/**
 * 提取运行时对象自身 bodyEntries 的归属匹配 key。
 *
 * @param object display.glb 运行时对象。
 * @returns 当前对象自身声明的 body 归属 key。
 */
function createRuntimeObjectBodyOwnershipLookupKeys(object: THREE.Object3D): string[] {
  const keys: string[] = []
  readOwnRuntimeBodyEntries(object).forEach((entry) => {
    [
      entry.bodyBindingId,
      entry.bodyId,
    ].forEach((bodyKey) => {
      createImportedBodyOwnershipLookupKeys(bodyKey).forEach((lookupKey) => {
        if (lookupKey && !keys.includes(lookupKey)) {
          keys.push(lookupKey)
        }
      })
    })
  })
  return keys
}

/**
 * 按对象自身 bodyBindingId 解析真实 assembly node 归属。
 *
 * @param object display.glb 运行时对象。
 * @param ownershipIndex bodyBinding 到 assembly node 的归属索引。
 * @returns 唯一归属的 assembly node ID；无法唯一确定时返回 undefined。
 */
function resolveRuntimeBodyOwnedAssemblyNodeId(
  object: THREE.Object3D,
  ownershipIndex: BodyBindingAssemblyNodeOwnershipIndex,
): string | undefined {
  const ownerNodeIds = new Set<string>()
  createRuntimeObjectBodyOwnershipLookupKeys(object).forEach((lookupKey) => {
    if (ownershipIndex.ambiguousLookupKeys.has(lookupKey)) {
      return
    }
    const nodeId = ownershipIndex.nodeIdByLookupKey.get(lookupKey)
    if (nodeId) {
      ownerNodeIds.add(nodeId)
    }
  })

  return ownerNodeIds.size === 1 ? [...ownerNodeIds][0] : undefined
}

/**
 * 收集装配节点 ID 到运行时 Object3D 的映射。
 *
 * @param root display.glb 运行时根节点。
 * @param ownershipIndex bodyBinding 到 assembly node 的归属索引。
 * @returns 装配节点对象索引。
 */
function collectObjectsByAssemblyNodeId(
  root: THREE.Object3D,
  ownershipIndex: BodyBindingAssemblyNodeOwnershipIndex,
): Map<string, THREE.Object3D[]> {
  const objectsByNodeId = new Map<string, THREE.Object3D[]>()
  root.traverse((object) => {
    const nodeId = resolveRuntimeBodyOwnedAssemblyNodeId(object, ownershipIndex)
      ?? readAssemblyNodeId(object)
    if (!nodeId) {
      return
    }
    const list = objectsByNodeId.get(nodeId) ?? []
    list.push(object)
    objectsByNodeId.set(nodeId, list)
  })
  return objectsByNodeId
}

/**
 * 按别名解析运行时对象最终归属的装配节点 ID。
 *
 * @param object display.glb 运行时对象。
 * @param nodeIdAliasByNodeId 被折叠节点到保留节点的映射。
 * @returns 最终装配节点 ID。
 */
function resolveRuntimeAssemblyNodeId(
  object: THREE.Object3D,
  nodeIdAliasByNodeId: Readonly<Record<string, string>>,
): string | undefined {
  const nodeId = readAssemblyNodeId(object)
  if (!nodeId) {
    return undefined
  }

  return resolveNormalizedAssemblyNodeId(nodeId, nodeIdAliasByNodeId)
}

/**
 * 收集归一化后的装配节点到运行时 Object3D 的映射。
 *
 * @param root display.glb 运行时根节点。
 * @param assembly `.puppy` 装配结构文档。
 * @param nodeIdAliasByNodeId 被折叠节点到保留节点的映射。
 * @returns 装配节点对象索引。
 */
function collectObjectsByNormalizedAssemblyNodeId(
  root: THREE.Object3D,
  assembly: PuppyAssemblyDocument,
  nodeIdAliasByNodeId: Readonly<Record<string, string>>,
): Map<string, THREE.Object3D[]> {
  const ownershipIndex = createBodyBindingAssemblyNodeOwnershipIndex(assembly, nodeIdAliasByNodeId)
  if (Object.keys(nodeIdAliasByNodeId).length === 0) {
    return collectObjectsByAssemblyNodeId(root, ownershipIndex)
  }

  const objectsByNodeId = new Map<string, THREE.Object3D[]>()
  root.traverse((object) => {
    const nodeId = resolveRuntimeBodyOwnedAssemblyNodeId(object, ownershipIndex)
      ?? resolveRuntimeAssemblyNodeId(object, nodeIdAliasByNodeId)
    if (!nodeId) {
      return
    }
    const list = objectsByNodeId.get(nodeId) ?? []
    list.push(object)
    objectsByNodeId.set(nodeId, list)
  })
  return objectsByNodeId
}

/**
 * 为装配节点选择对应的运行时 Object3D。
 *
 * @param input 选择输入。
 * @returns 运行时 Object3D。
 */
function resolveRuntimeObject(input: {
  node: PuppyAssemblyNode
  assembly: PuppyAssemblyDocument
  objectsByNodeId: Map<string, THREE.Object3D[]>
  root: THREE.Object3D
}): THREE.Object3D {
  const candidates = input.objectsByNodeId.get(input.node.nodeId) ?? []
  const host = candidates.find(isAssemblyNodeHost) ?? candidates[0]
  if (host) {
    return host
  }
  if (input.node.nodeId === input.assembly.rootNodeId) {
    return input.root
  }
  throw new Error(`PuppyCAD assembly.json 节点缺少 display.glb 运行时对象: ${input.node.nodeId}`)
}

/**
 * 读取导出阶段记录的选择代理源对象 ID。
 *
 * @deprecated 仅用于 v4/v5 的 Object3D UUID 选择引用；兼容回退计划于 2026-08-11 移除。
 *
 * @param node 装配节点。
 * @returns 源场景选择代理对象 ID。
 */
function readSelectionSourceObjectId(node: PuppyAssemblyNode): string | undefined {
  const value = node.extensions?.selectionSourceObjectId
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

/**
 * 读取 v6 装配节点的稳定选择代理节点 ID。
 *
 * @param node 装配节点。
 * @returns 稳定 selection assembly node ID；不存在时返回 undefined。
 */
function readSelectionAssemblyNodeId(node: PuppyAssemblyNode): string | undefined {
  const value = node.extensions?.selectionAssemblyNodeId
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

//------ 装配体引用复制族 开始------
/**
 * 判断未知值是否为普通记录。
 *
 * @param value 待判断值。
 * @returns 是否为普通记录。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

/**
 * 读取非空字符串。
 *
 * @param value 待读取值。
 * @returns 非空字符串；无效时返回空字符串。
 */
function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * 读取引用复制族角色。
 *
 * @param value 待读取值。
 * @returns 有效角色；无效时返回 null。
 */
function readReferenceLineageRole(value: unknown): AssemblyReferenceCopyLineageRole | null {
  return value === AssemblyReferenceCopyLineageRole.OriginOccurrence
    || value === AssemblyReferenceCopyLineageRole.ReferenceOccurrence
      ? value
      : null
}

/**
 * 从装配节点扩展读取引用复制族元数据。
 *
 * @param node 装配节点。
 * @returns 引用复制族元数据；不存在时返回 null。
 */
function readReferenceLineageMetadata(node: PuppyAssemblyNode): AssemblyReferenceCopyLineageMetadata | null {
  const value = node.extensions?.referenceLineage
  if (!isRecord(value)) {
    return null
  }

  const referenceFamilyId = readString(value.referenceFamilyId)
  const definitionId = readString(value.definitionId)
  const originOccurrenceId = readString(value.originOccurrenceId)
  const role = readReferenceLineageRole(value.role)
  if (!referenceFamilyId || !definitionId || !originOccurrenceId || !role) {
    return null
  }

  const copiedFromOccurrenceId = readString(value.copiedFromOccurrenceId)
  return {
    referenceFamilyId,
    definitionId,
    originOccurrenceId,
    role,
    ...(copiedFromOccurrenceId ? { copiedFromOccurrenceId } : {}),
  }
}
//------ 装配体引用复制族 结束------

/**
 * 建立源 Object3D UUID 到 assembly node ID 的索引。
 *
 * @deprecated 仅用于 v4/v5 的 sourceObjectId；兼容索引计划于 2026-08-11 移除。
 *
 * @param assembly 装配结构文档。
 * @returns 源对象 ID 到装配节点 ID 的索引。
 */
function createNodeIdBySourceObjectId(assembly: PuppyAssemblyDocument): Map<string, string> {
  const nodeIdBySourceObjectId = new Map<string, string>()
  assembly.nodes.forEach((node) => {
    const sourceObjectId = node.sourceObjectId?.trim()
    if (sourceObjectId && !nodeIdBySourceObjectId.has(sourceObjectId)) {
      nodeIdBySourceObjectId.set(sourceObjectId, node.nodeId)
    }
  })
  return nodeIdBySourceObjectId
}

/**
 * 解析 `.puppy` 装配节点的运行时对象绑定。
 *
 * @param input 绑定解析输入。
 * @returns assembly node ID 到运行时绑定的索引。
 */
function createRuntimeBindingByAssemblyNodeId(input: {
  assembly: PuppyAssemblyDocument
  objectsByNodeId: Map<string, THREE.Object3D[]>
  root: THREE.Object3D
}): Map<string, PuppyAssemblyRuntimeBinding> {
  const runtimeObjectByNodeId = new Map<string, THREE.Object3D>()
  input.assembly.nodes.forEach((node) => {
    runtimeObjectByNodeId.set(node.nodeId, resolveRuntimeObject({
      node,
      assembly: input.assembly,
      objectsByNodeId: input.objectsByNodeId,
      root: input.root,
    }))
  })

  const nodeIdBySourceObjectId = createNodeIdBySourceObjectId(input.assembly)
  const bindingByNodeId = new Map<string, PuppyAssemblyRuntimeBinding>()
  input.assembly.nodes.forEach((node) => {
    const object = runtimeObjectByNodeId.get(node.nodeId)
    if (!object) {
      return
    }

    const stableSelectionNodeId = readSelectionAssemblyNodeId(node)
    const selectionSourceObjectId = readSelectionSourceObjectId(node)
    const resolvedSelectionNodeId = stableSelectionNodeId && runtimeObjectByNodeId.has(stableSelectionNodeId)
      ? stableSelectionNodeId
      // v4/v5 selectionSourceObjectId 回退计划于 2026-08-11 移除。
      : selectionSourceObjectId
        ? nodeIdBySourceObjectId.get(selectionSourceObjectId)
        : undefined
    /**
     * 非根节点的选择代理不能退回到根装配体。
     * 一旦 root proxy 写入 metadata，后续场景点击、设计树回选和拖拽都会把整机当成当前零件。
     */
    const selectionNodeId = (
      resolvedSelectionNodeId
      && !(node.nodeId !== input.assembly.rootNodeId && resolvedSelectionNodeId === input.assembly.rootNodeId)
    )
      ? resolvedSelectionNodeId
      : undefined
    const selectionObject = selectionNodeId
      ? runtimeObjectByNodeId.get(selectionNodeId) ?? object
      : object

    bindingByNodeId.set(node.nodeId, {
      object,
      selectionObject,
      selectionObjectId: selectionObject.uuid,
    })
  })
  return bindingByNodeId
}

/**
 * 创建兜底 hierarchy meta。
 *
 * @param node 装配节点。
 * @returns hierarchy meta。
 */
function createFallbackMeta(node: PuppyAssemblyNode): GlbHierarchyNodeMeta {
  return {
    semanticKind: node.semanticKind ?? 'unknown',
    assemblyNodeId: node.nodeId,
    hasGeometry: node.displayNodeIds.length > 0 || node.geometryObjectIds.length > 0,
    hasDirectGeometry: node.displayNodeIds.length > 0,
    bodyCount: node.bodyBindingIds.length > 0 ? node.bodyBindingIds.length : undefined,
    materialNames: [],
  }
}

/**
 * 读取 display.glb 运行时对象上由 topology source 写回的 body 条目。
 *
 * 设计说明：
 * 1. `.puppy` 导入先由 `buildPuppyImportedTopologySource()` 将 display body 绑定
 *    写入可见 mesh 的 `userData.importedPartBodyEntries`；
 * 2. `assembly.json` 的 node meta 可能来自原生特征结果体，未必包含 bodyEntries；
 * 3. hierarchy 是设计树、树选高亮与鼠标拓扑选择的公共语义源，因此这里必须从
 *    已恢复的运行时对象补齐 bodyEntries，不能把这件事留给 web-client 兜底。
 *
 * @param object display.glb 中当前装配节点对应的运行时对象。
 * @returns 去重后的 body 条目。
 */
function collectRuntimeBodyEntries(object: THREE.Object3D): ImportedBodyEntry[] {
  const collected: ImportedBodyEntry[] = []
  object.traverse((child) => {
    const bodyEntries = parseImportedBodyEntries(child.userData?.importedPartBodyEntries)
    if (bodyEntries.length > 0) {
      collected.push(...bodyEntries)
    }
  })
  return mergeBodyEntriesById(collected)
}

/**
 * 根据 assembly node 的 bodyBindingIds 创建最后兜底 body 条目。
 *
 * 说明：优先级低于 topology source 写回的 bodyEntries，仅用于损坏/旧链路中避免
 * hierarchy 完全丢失 body 节点；显示名在导出端已经尽量写入 topology source。
 *
 * @param node 装配节点。
 * @returns 兜底 body 条目。
 */
function createFallbackBodyEntries(node: PuppyAssemblyNode): ImportedBodyEntry[] {
  return node.bodyBindingIds
    .map((bodyBindingId, index): ImportedBodyEntry | null => {
      const id = bodyBindingId.trim()
      if (!id) {
        return null
      }
      return createFallbackImportedBodyEntry({
        bodyId: id,
        bodyBindingId: id,
        fallbackIndex: index + 1,
      })
    })
    .filter((entry): entry is ImportedBodyEntry => entry !== null)
}

/**
 * 判断当前 assembly node 是否拥有恢复 bodyEntries 的语义边界。
 *
 * 设计说明：
 * 1. 多实体零件导出时会生成 `semanticKind: 'part'` 的合成根，它没有直接 bodyBindingIds，
 *    但它的子树 bodyEntries 必须聚合到零件实体文件夹；
 * 2. 装配/子装配节点没有直接 bodyBindingIds 时只表达层级容器，不能从子树聚合实体，
 *    否则导入后设计树会在装配节点下凭空生成子零件 body 节点。
 *
 * @param node 当前装配层级节点。
 * @returns 是否允许恢复 bodyEntries。
 */
function canRestoreBodyEntriesForAssemblyNode(node: PuppyAssemblyNode): boolean {
  if (node.semanticKind === 'part') {
    return true
  }
  if (node.semanticKind === 'assembly') {
    return false
  }
  return node.bodyBindingIds.some((bodyBindingId) => bodyBindingId.trim().length > 0)
}

/**
 * 按 assembly node 声明的 bodyBindingIds 过滤运行时 body 条目。
 *
 * 设计说明：
 * 选择代理可能是上级装配对象，直接遍历 selectionObject 会看到兄弟实体。
 * 这里用 assembly.json 的 bodyBindingIds 作为归属边界，保证 hierarchy 节点只恢复自己拥有的 body。
 *
 * @param entries 待过滤 body 条目。
 * @param node 当前装配节点。
 * @returns 过滤后的 body 条目。
 */
function filterBodyEntriesByAssemblyNode(
  entries: ImportedBodyEntry[],
  node: PuppyAssemblyNode,
): ImportedBodyEntry[] {
  const ownerBodyBindingIds = node.bodyBindingIds
    .map((bodyBindingId) => bodyBindingId.trim())
    .filter((bodyBindingId) => bodyBindingId.length > 0)
  if (ownerBodyBindingIds.length === 0) {
    return entries
  }

  const ownerLookupKeys = new Set(ownerBodyBindingIds.flatMap((bodyBindingId) => (
    createImportedBodyOwnershipLookupKeys(bodyBindingId)
  )))
  return entries.filter((entry) => (
    [
      entry.bodyId,
      entry.bodyBindingId,
    ].some((bodyKey) => (
      createImportedBodyOwnershipLookupKeys(bodyKey).some((lookupKey) => ownerLookupKeys.has(lookupKey))
    ))
  ))
}

/**
 * 判断 bodyBindingId 是否携带导出几何作用域。
 *
 * @param bodyBindingId 待判断的 bodyBindingId。
 * @returns 是否携带导出几何作用域。
 */
function hasExportScopedBodyBindingId(bodyBindingId: string): boolean {
  return bodyBindingId.includes('::geometry:') || bodyBindingId.includes('::occurrence:')
}

/**
 * 当前节点的拥有者 bodyBindingIds 是否需要局部回填到旧实体条目。
 *
 * @param ownerBodyBindingIds 当前 assembly node 拥有的 bodyBindingIds。
 * @returns 是否需要局部回填。
 */
function shouldEnrichLegacyBodyEntriesWithOwnedBindingIds(ownerBodyBindingIds: readonly string[]): boolean {
  return ownerBodyBindingIds.some(hasExportScopedBodyBindingId)
}

/**
 * 为单个实体条目创建归属匹配 key。
 *
 * @param entry 实体条目。
 * @returns 去重后的归属 key。
 */
function createBodyEntryOwnershipLookupKeys(entry: ImportedBodyEntry): string[] {
  return Array.from(new Set([
    ...createImportedBodyOwnershipLookupKeys(entry.bodyBindingId),
    ...createImportedBodyOwnershipLookupKeys(entry.bodyId),
    ...createImportedBodyOwnershipLookupKeys(entry.label),
  ]))
}

/**
 * 查找旧实体条目在当前节点边界内对应的精确 bodyBindingId。
 *
 * @param input 查找输入。
 * @returns 命中的 bodyBindingId；未命中时返回 undefined。
 */
function resolveOwnedBodyBindingIdForLegacyEntry(input: {
  readonly entry: ImportedBodyEntry
  readonly entryIndex: number
  readonly entries: readonly ImportedBodyEntry[]
  readonly ownerBodyBindingIds: readonly string[]
  readonly usedBodyBindingIds: ReadonlySet<string>
}): string | undefined {
  const entryLookupKeys = createBodyEntryOwnershipLookupKeys(input.entry)
  const directMatch = input.ownerBodyBindingIds.find((bodyBindingId) => (
    !input.usedBodyBindingIds.has(bodyBindingId)
    && createImportedBodyOwnershipLookupKeys(bodyBindingId).some((lookupKey) => entryLookupKeys.includes(lookupKey))
  ))
  if (directMatch) {
    return directMatch
  }

  /**
   * 带 `::geometry:` 的 bodyBindingId 不能通过全局稳定 key 合并，否则同源装配实例会再次共享。
   * 但在当前 assembly node 内，bodyBindingIds 与旧 bodyEntries 数量一致时可按导出顺序补全精确身份。
   */
  if (input.entries.length === input.ownerBodyBindingIds.length) {
    const positionalMatch = input.ownerBodyBindingIds[input.entryIndex]
    return positionalMatch && !input.usedBodyBindingIds.has(positionalMatch)
      ? positionalMatch
      : undefined
  }
  return undefined
}

/**
 * 在当前 assembly node 边界内为旧 bodyEntries 回填精确 bodyBindingId。
 *
 * 设计说明：同源插入件需要用 `::geometry:` 区分不同 occurrence，全局等价规则不能剥离该作用域。
 * 旧包的可读 bodyEntries 往往只有稳定 bodyId，必须在节点拥有者边界内补上精确 binding，
 * 才能和运行时 mesh 条目合并为一个可读实体。
 *
 * @param entries 旧实体条目。
 * @param node 当前 assembly node。
 * @returns 回填后的实体条目。
 */
function enrichLegacyBodyEntriesWithAssemblyNodeOwnedBindingIds(
  entries: ImportedBodyEntry[] | undefined,
  node: PuppyAssemblyNode,
): ImportedBodyEntry[] | undefined {
  const ownerBodyBindingIds = node.bodyBindingIds
    .map((bodyBindingId) => bodyBindingId.trim())
    .filter((bodyBindingId) => bodyBindingId.length > 0)
  if (
    !entries
    || entries.length === 0
    || ownerBodyBindingIds.length === 0
    || !shouldEnrichLegacyBodyEntriesWithOwnedBindingIds(ownerBodyBindingIds)
  ) {
    return entries
  }

  const usedBodyBindingIds = new Set<string>()
  return entries.map((entry, entryIndex) => {
    if (typeof entry.bodyBindingId === 'string' && entry.bodyBindingId.trim().length > 0) {
      usedBodyBindingIds.add(entry.bodyBindingId.trim())
      return entry
    }

    const bodyBindingId = resolveOwnedBodyBindingIdForLegacyEntry({
      entry,
      entryIndex,
      entries,
      ownerBodyBindingIds,
      usedBodyBindingIds,
    })
    if (!bodyBindingId) {
      return entry
    }

    usedBodyBindingIds.add(bodyBindingId)
    return {
      ...entry,
      bodyBindingId,
    }
  })
}

/**
 * 恢复单实体零件的可读实体名称。
 *
 * 设计说明：离线包 display mesh 的运行时 body 条目可能只能恢复到 `mesh:...` 绑定 ID。
 * 对单实体零件而言，SolidWorks 设计树通常以零件/实体的可读名称展示；继续暴露技术 ID
 * 会污染后续保存的 hierarchy meta，并在插入同源装配体后形成不可读实体节点。
 *
 * @param node 当前装配节点。
 * @param bodyEntries 已解析的实体条目。
 * @returns 修正显示名后的实体条目。
 */
function restoreSingleBodyReadableLabel(
  node: PuppyAssemblyNode,
  bodyEntries: ImportedBodyEntry[],
): ImportedBodyEntry[] {
  const readableNodeName = node.name.trim()
  if (
    node.semanticKind !== 'part'
    || bodyEntries.length !== 1
    || !readableNodeName
    || !isRuntimeImportedBodyEntryLabel(bodyEntries[0]!)
  ) {
    return bodyEntries
  }

  return [{
    ...bodyEntries[0]!,
    label: readableNodeName,
  }]
}

/**
 * 为 hierarchy meta 解析完整 body 条目。
 *
 * @param input 解析输入。
 * @returns 已按 bodyId 去重的 body 条目。
 */
function resolveRuntimeBodyEntries(input: {
  node: PuppyAssemblyNode
  base: GlbHierarchyNodeMeta
  binding: PuppyAssemblyRuntimeBinding
}): ImportedBodyEntry[] {
  if (!canRestoreBodyEntriesForAssemblyNode(input.node)) {
    return []
  }
  const selectionObjectEntries = input.binding.selectionObject === input.binding.object
    ? undefined
    : collectRuntimeBodyEntries(input.binding.selectionObject)
  const baseBodyEntries = enrichLegacyBodyEntriesWithAssemblyNodeOwnedBindingIds(
    input.base.bodyEntries,
    input.node,
  )
  const bodyEntries = filterBodyEntriesByAssemblyNode(mergeBodyEntriesById(
    baseBodyEntries,
    collectRuntimeBodyEntries(input.binding.object),
    selectionObjectEntries,
    createFallbackBodyEntries(input.node),
  ), input.node)
  return restoreSingleBodyReadableLabel(input.node, bodyEntries)
    .map((entry, index) => normalizeImportedBodyEntryDisplay(entry, index + 1))
}

/**
 * 判断运行时对象子树是否包含可渲染 mesh。
 *
 * @param object display.glb 中的运行时对象。
 * @returns 当前对象或子孙对象是否包含 mesh。
 */
function hasRuntimeMeshInSubtree(object: THREE.Object3D): boolean {
  let hasMesh = false
  object.traverse((child) => {
    if (child.type === 'Mesh') {
      hasMesh = true
    }
  })
  return hasMesh
}

/**
 * 判断运行时对象是否直接承载几何。
 *
 * @param object display.glb 中的运行时对象。
 * @returns 当前对象自身或直接子对象是否是 mesh。
 */
function hasDirectRuntimeMesh(object: THREE.Object3D): boolean {
  return object.type === 'Mesh'
    || object.children.some((child) => child.type === 'Mesh')
}

/**
 * 判断 assembly node 是否声明了直接几何归属。
 *
 * @param node assembly.json 中的节点。
 * @param bodyEntries 已恢复的 body 条目。
 * @returns 节点是否应被视为直接几何宿主。
 */
function hasDeclaredDirectGeometry(
  node: PuppyAssemblyNode,
  bodyEntries: ImportedBodyEntry[],
): boolean {
  return node.displayNodeIds.length > 0
    || node.geometryObjectIds.length > 0
    || node.bodyBindingIds.length > 0
    || bodyEntries.length > 0
}

/**
 * 创建运行时 hierarchy meta。
 *
 * @param input 创建输入。
 * @returns 运行时 hierarchy meta。
 */
function createRuntimeMeta(input: {
  node: PuppyAssemblyNode
  binding: PuppyAssemblyRuntimeBinding
}): GlbHierarchyNodeMeta {
  const base = input.node.meta
    ? cloneGlbHierarchyUserDataMeta(input.node.meta)
    : createFallbackMeta(input.node)
  const bodyEntries = resolveRuntimeBodyEntries({
    node: input.node,
    base,
    binding: input.binding,
  })
  const canRestoreBodyEntries = canRestoreBodyEntriesForAssemblyNode(input.node)
  const bodyHighlightTargetsById = resolveBodyHighlightTargetsById(
    input.binding.selectionObject,
    bodyEntries,
  )
  const hasDirectGeometry = base.hasDirectGeometry
    || hasDeclaredDirectGeometry(input.node, bodyEntries)
    || hasDirectRuntimeMesh(input.binding.object)

  return {
    ...base,
    semanticKind: input.node.semanticKind ?? base.semanticKind ?? 'unknown',
    assemblyNodeId: input.node.nodeId,
    hasGeometry: base.hasGeometry
      || hasDirectGeometry
      || hasRuntimeMeshInSubtree(input.binding.object),
    hasDirectGeometry,
    bodyCount: canRestoreBodyEntries
      ? bodyEntries.length > 0 ? bodyEntries.length : base.bodyCount
      : undefined,
    bodyEntries: canRestoreBodyEntries
      ? bodyEntries.length > 0 ? bodyEntries : base.bodyEntries
      : undefined,
    bodyPrimitiveIndicesById: canRestoreBodyEntries ? base.bodyPrimitiveIndicesById : undefined,
    bodyHighlightTargetsById: canRestoreBodyEntries
      ? bodyHighlightTargetsById ?? base.bodyHighlightTargetsById
      : undefined,
    materialNames: [...(base.materialNames ?? [])],
    selectionObjectId: input.node.selectable ? input.binding.selectionObjectId : base.selectionObjectId,
  }
}

/**
 * 解析导入后虚拟 Body 节点应该回选到的显示对象。
 *
 * @param input 选择代理解析输入。
 * @returns Body 对应的显示 Mesh UUID；缺少精确高亮目标时回退父级对象。
 */
function resolveRuntimeBodySelectionObjectId(input: {
  parentObjectId: string
  parentMeta: GlbHierarchyNodeMeta
  bodyId: string
}): string {
  const highlightTargets = input.parentMeta.bodyHighlightTargetsById?.[input.bodyId]
  if (!Array.isArray(highlightTargets)) {
    return input.parentObjectId
  }

  const targetObjectId = highlightTargets
    .map((target) => target.objectId?.trim() ?? '')
    .find((objectId) => objectId.length > 0)

  /**
   * `.puppy` 的一个零件节点可以承载多个特征实体，Body 节点必须回选到自身
   * display mesh，否则设计树实体选中会退回父级零件，场景点线面也无法反查到实体。
   */
  return targetObjectId ?? input.parentObjectId
}

/**
 * 为 `.puppy` 导入层级创建与普通 GLB 导入一致的虚拟 Body 子节点。
 *
 * 设计说明：
 * FeatureManager 已经把 `nodeType: 'Body'` 折叠到「实体(N)」文件夹并注册
 * `bodyNodeBindings`。离线包导入必须在 cad-core 层恢复同一套 hierarchy 语义，
 * 否则 web-client 只能看到零件容器，看不到可选中的实体节点。
 *
 * @param input 创建输入。
 * @returns 虚拟 Body hierarchy 节点。
 */
function createRuntimeBodyNodes(input: {
  parentObjectId: string
  parentPath: string
  parentDepth: number
  parentSelectable: boolean
  parentMeta: GlbHierarchyNodeMeta
}): GlbHierarchyNode[] {
  const bodyEntries = Array.isArray(input.parentMeta.bodyEntries)
    ? input.parentMeta.bodyEntries.map((entry, index) => (
        normalizeImportedBodyEntryDisplay(entry, index + 1)
      ))
    : []
  return bodyEntries.map((bodyEntry, index) => {
    const bodyObjectId = `${input.parentObjectId}::body::${index + 1}`
    const selectionObjectId = resolveRuntimeBodySelectionObjectId({
      parentObjectId: input.parentObjectId,
      parentMeta: input.parentMeta,
      bodyId: bodyEntry.bodyId,
    })
    const bodyMeta: GlbHierarchyNodeMeta = {
      semanticKind: 'part',
      hasGeometry: input.parentMeta.hasGeometry,
      hasDirectGeometry: input.parentMeta.hasDirectGeometry,
      bodyCount: 1,
      bodyEntries: [bodyEntry],
      bodyPrimitiveIndicesById: input.parentMeta.bodyPrimitiveIndicesById,
      bodyHighlightTargetsById: input.parentMeta.bodyHighlightTargetsById,
      materialNames: [...(input.parentMeta.materialNames ?? [])],
      materialName: input.parentMeta.materialName,
      externalFileUuid: input.parentMeta.externalFileUuid,
      externalAssemblyName: input.parentMeta.externalAssemblyName,
      selectionObjectId,
      highlightTargets: input.parentMeta.bodyHighlightTargetsById?.[bodyEntry.bodyId],
    }
    return {
      objectId: bodyObjectId,
      parentObjectId: input.parentObjectId,
      path: `${input.parentPath}/${bodyEntry.label}`,
      name: bodyEntry.label,
      nodeType: 'Body',
      selectable: input.parentSelectable,
      depth: input.parentDepth + 1,
      meta: bodyMeta,
      children: [],
    }
  })
}

/**
 * 从 Mate 实体中读取可用于 body 级选择恢复的目标。
 *
 * @param entity Mate 实体引用。
 * @returns bodyBindingId 与组件选择目标；缺少稳定拓扑引用时返回 `null`。
 */
function resolveBodyBindingSelectionTargetFromMateEntity(
  entity: MateEntityRef | undefined,
): { bodyBindingId: string; selectionTarget: SelectionTarget } | null {
  const bodyBindingId = entity?.topologyRef?.bodyBindingId?.trim()
  const assemblyNodeId = (
    entity?.componentInstanceId
    ?? entity?.assemblyNodeId
    ?? entity?.topologyRef?.assemblyNodeId
  )?.trim()
  if (!bodyBindingId || !assemblyNodeId) {
    return null
  }

  return {
    bodyBindingId,
    selectionTarget: {
      assemblyNodeId,
      partUuid: entity?.partUuid ?? entity?.topologyRef?.partUuid,
    },
  }
}

/**
 * 为 `.puppy` 导入创建 bodyBindingId 到 Mate 组件选择目标的索引。
 *
 * @param assembly assembly.json 文档。
 * @returns bodyBindingId 到选择目标的稳定映射。
 */
function createBodyBindingSelectionTargetIndex(
  assembly: PuppyAssemblyDocument,
): BodyBindingSelectionTargetIndex {
  const index: BodyBindingSelectionTargetIndex = {}
  const appendTarget = (bodyBindingId: string, selectionTarget: SelectionTarget): void => {
    createImportedBodyOwnershipLookupKeys(bodyBindingId).forEach((lookupKey) => {
      const targets = index[lookupKey] ?? []
      if (!targets.some((item) => item.assemblyNodeId === selectionTarget.assemblyNodeId)) {
        index[lookupKey] = [...targets, selectionTarget]
      }
    })
  }
  const mates = Array.isArray(assembly.mates.mates) ? assembly.mates.mates : []
  mates.forEach((mate) => {
    [
      mate.firstEntity,
      mate.secondEntity,
      mate.referenceEntity,
    ].forEach((entity) => {
      const target = resolveBodyBindingSelectionTargetFromMateEntity(entity)
      if (!target) {
        return
      }
      appendTarget(target.bodyBindingId, target.selectionTarget)
    })
  })
  return index
}

/**
 * 按当前装配节点作用域解析 bodyBindingId 的选择目标。
 *
 * @param bodyBindingId 当前节点覆盖的 bodyBindingId。
 * @param node 当前离线包装配节点。
 * @param index bodyBindingId 到候选选择目标的索引。
 * @returns 与当前节点最匹配的选择目标；无明确匹配时返回 null。
 */
function resolveScopedBodyBindingSelectionTarget(
  bodyBindingId: string,
  node: PuppyAssemblyNode,
  index: BodyBindingSelectionTargetIndex,
): SelectionTarget | null {
  const lookupKeys = createImportedBodyOwnershipLookupKeys(bodyBindingId)
  const lookupKeySet = new Set(lookupKeys)
  const stableBodyBindingId = resolveStableImportedBodyBindingId(bodyBindingId)
  const candidates = [
    ...lookupKeys.flatMap((lookupKey) => index[lookupKey] ?? []),
    ...Object.entries(index)
      .filter(([candidateBodyBindingId]) => (
        resolveStableImportedBodyBindingId(candidateBodyBindingId) === stableBodyBindingId
        || createImportedBodyOwnershipLookupKeys(candidateBodyBindingId).some((lookupKey) => (
          lookupKeySet.has(lookupKey)
        ))
      ))
      .flatMap(([, targets]) => targets),
  ]
  const uniqueCandidates = candidates.filter((candidate, candidateIndex) => (
    candidates.findIndex((item) => item.assemblyNodeId === candidate.assemblyNodeId) === candidateIndex
  ))
  // meta.selectionObjectId/sourceObjectId 仅兼容 v4/v5，计划于 2026-08-11 从匹配候选中移除。
  const nodeIds = new Set([
    node.nodeId,
    node.path,
    node.meta?.selectionObjectId,
    node.sourceObjectId,
  ].filter((item): item is string => typeof item === 'string' && item.length > 0))
  const matchedCandidate = uniqueCandidates.find((candidate) => nodeIds.has(candidate.assemblyNodeId))
  if (matchedCandidate) {
    return matchedCandidate
  }

  return uniqueCandidates.length === 1 ? uniqueCandidates[0] ?? null : null
}

/**
 * 提取当前装配节点所覆盖 body 的选择目标覆盖。
 *
 * @param node 装配节点。
 * @param index 全局 bodyBinding 选择目标索引。
 * @returns 当前节点需要写入 metadata 的覆盖映射。
 */
function resolveNodeBodyBindingSelectionTargets(
  node: PuppyAssemblyNode,
  index: BodyBindingSelectionTargetIndex,
): Record<string, SelectionTarget> | undefined {
  const targets: Record<string, SelectionTarget> = {}
  node.bodyBindingIds.forEach((bodyBindingId) => {
    const target = resolveScopedBodyBindingSelectionTarget(bodyBindingId, node, index)
    if (!target) {
      return
    }
    targets[bodyBindingId] = target
  })
  return Object.keys(targets).length > 0 ? targets : undefined
}

/**
 * 判断两个 body 绑定 ID 是否表达同一个导入实体。
 *
 * @param left 左侧 body ID。
 * @param right 右侧 body ID。
 * @returns 两个 ID 是否等价。
 */
function areRuntimeBodyIdsEquivalent(left: string, right: string): boolean {
  const leftKeys = new Set(createImportedBodyOwnershipLookupKeys(left))
  return createImportedBodyOwnershipLookupKeys(right).some((lookupKey) => leftKeys.has(lookupKey))
}

/**
 * 按 body ID 从父级 hierarchy meta 中查找 body 条目。
 *
 * @param meta 父级运行时 meta。
 * @param bodyId 目标 body ID。
 * @returns 匹配的 body 条目；缺少时用 bodyId 生成兜底条目。
 */
function resolveBodyEntryById(
  meta: GlbHierarchyNodeMeta,
  bodyId: string,
  fallbackIndex = 1,
): ImportedBodyEntry {
  const matchedEntry = meta.bodyEntries?.find((entry) => areRuntimeBodyIdsEquivalent(entry.bodyId, bodyId))
  if (matchedEntry) {
    return normalizeImportedBodyEntryDisplay(matchedEntry, fallbackIndex)
  }

  return createFallbackImportedBodyEntry({
      bodyId,
      bodyBindingId: bodyId,
      fallbackIndex,
    })
}

/**
 * 从当前运行时对象自身读取 body 条目。
 *
 * 说明：这里刻意只读当前对象自身，不遍历子树。父级 part/group 仍然表达零件容器，
 * 真实 visible mesh 才表达具体 feature body；混用会把实体选择再次回收到父级。
 *
 * @param object display.glb 中的运行时对象。
 * @returns 当前对象自身承载的 body 条目。
 */
function readOwnRuntimeBodyEntries(object: THREE.Object3D): ImportedBodyEntry[] {
  return parseImportedBodyEntries(object.userData?.importedPartBodyEntries)
}

/**
 * 解析当前运行时对象直接拥有的 body 条目。
 *
 * @param object display.glb 中的运行时对象。
 * @param meta 父级 hierarchy meta。
 * @returns 当前对象拥有的 body 条目。
 */
function resolveObjectOwnedBodyEntries(
  object: THREE.Object3D,
  meta: GlbHierarchyNodeMeta | undefined,
): ImportedBodyEntry[] {
  if (!meta) {
    return []
  }
  const directBodyEntries = readOwnRuntimeBodyEntries(object)
  const highlightedBodyEntries = Object.entries(meta.bodyHighlightTargetsById ?? {})
    .filter(([, targets]) => targets.some((target) => target.objectId === object.uuid))
    .map(([bodyId], index) => resolveBodyEntryById(meta, bodyId, index + 1))
  return mergeBodyEntriesById(directBodyEntries, highlightedBodyEntries)
}

/**
 * 按当前对象拥有的 body 条目裁剪 body 高亮目标表。
 *
 * @param meta 父级 hierarchy meta。
 * @param bodyEntries 当前对象拥有的 body 条目。
 * @returns 只包含当前对象 body 的高亮目标表。
 */
function createScopedBodyHighlightTargetsById(
  meta: GlbHierarchyNodeMeta,
  bodyEntries: ImportedBodyEntry[],
): GlbHierarchyNodeMeta['bodyHighlightTargetsById'] {
  const sourceTargetsById = meta.bodyHighlightTargetsById
  if (!sourceTargetsById || bodyEntries.length === 0) {
    return undefined
  }

  const scopedTargetsById: NonNullable<GlbHierarchyNodeMeta['bodyHighlightTargetsById']> = {}
  bodyEntries.forEach((bodyEntry) => {
    const matched = Object.entries(sourceTargetsById)
      .find(([bodyId]) => areRuntimeBodyIdsEquivalent(bodyId, bodyEntry.bodyId))
    if (!matched) {
      return
    }
    scopedTargetsById[bodyEntry.bodyId] = matched[1]
  })
  return Object.keys(scopedTargetsById).length > 0 ? scopedTargetsById : undefined
}

/**
 * 按当前对象拥有的 body 条目裁剪 body 选择目标表。
 *
 * @param targetsByBodyBindingId 父级 body 选择目标表。
 * @param bodyEntries 当前对象拥有的 body 条目。
 * @returns 只包含当前对象 body 的选择目标表。
 */
function createScopedBodyBindingSelectionTargetsById(
  targetsByBodyBindingId: Record<string, SelectionTarget> | undefined,
  bodyEntries: ImportedBodyEntry[],
): Record<string, SelectionTarget> | undefined {
  if (!targetsByBodyBindingId || bodyEntries.length === 0) {
    return undefined
  }

  const scopedTargetsById: Record<string, SelectionTarget> = {}
  bodyEntries.forEach((bodyEntry) => {
    const matched = Object.entries(targetsByBodyBindingId)
      .find(([bodyBindingId]) => areRuntimeBodyIdsEquivalent(bodyBindingId, bodyEntry.bodyId))
    if (!matched) {
      return
    }
    scopedTargetsById[bodyEntry.bodyId] = matched[1]
  })
  return Object.keys(scopedTargetsById).length > 0 ? scopedTargetsById : undefined
}

/**
 * 判断是否需要在 Object3D hierarchy 镜像中保留父级完整 bodyEntries。
 *
 * 设计说明：
 * 旧版 `.puppy` 可能在 `assembly.json` 中已经声明 display/body 绑定，
 * 但 node meta 仍写成 `hasGeometry=false`。这类包的可见 mesh 是同一个零件节点的
 * display 承载体，而不是独立零件语义节点；如果把 hierarchy meta 裁剪成单 body，
 * imported-model 后续只能从任一 mesh 看到局部实体，设计树就无法把离线包零件恢复成
 * 与普通导入零件一致的多实体 part。
 *
 * @param node 当前 assembly 节点。
 * @returns 是否应保留父级完整 bodyEntries。
 */
function shouldKeepParentBodyEntriesInObjectHierarchy(node: PuppyAssemblyNode): boolean {
  const legacyMetaDeclaresNoGeometry = node.meta?.hasGeometry === false
    && node.meta?.hasDirectGeometry === false
  return legacyMetaDeclaresNoGeometry
    && node.semanticKind === 'part'
    && node.displayNodeIds.length > 0
    && node.bodyBindingIds.length > 0
}

/**
 * 为真实 visible mesh 创建对象级 hierarchy meta。
 *
 * 设计说明：
 * `.puppy` 的 part 根节点可以同时承载多个 feature body。父级 group 需要保留完整零件
 * meta 供实体文件夹展示；但真正参与射线拾取的 visible mesh 必须拥有自身 body 的 meta
 * 与 selection proxy，否则点/线/面选择和设计树实体高亮都会回收到父级 group。
 *
 * @param object display.glb 中的运行时对象。
 * @param meta 父级 hierarchy meta。
 * @returns 对象级 meta；当前对象不直接承载 body 时返回父级 meta。
 */
function createRuntimeObjectScopedMeta(
  object: THREE.Object3D,
  meta: GlbHierarchyNodeMeta | undefined,
  shouldKeepParentBodyEntries: boolean,
): GlbHierarchyNodeMeta | undefined {
  const ownedBodyEntries = resolveObjectOwnedBodyEntries(object, meta)
  if (!meta || ownedBodyEntries.length === 0) {
    return meta
  }

  const bodyHighlightTargetsById = createScopedBodyHighlightTargetsById(meta, ownedBodyEntries)
  const highlightTargets = Object.values(bodyHighlightTargetsById ?? {}).flat()
  if (shouldKeepParentBodyEntries) {
    return {
      ...meta,
      selectionObjectId: object.uuid,
      highlightTargets: highlightTargets.length > 0 ? highlightTargets : meta.highlightTargets,
    }
  }

  return {
    ...meta,
    bodyCount: ownedBodyEntries.length,
    bodyEntries: ownedBodyEntries,
    bodyHighlightTargetsById,
    selectionObjectId: object.uuid,
    highlightTargets: highlightTargets.length > 0 ? highlightTargets : meta.highlightTargets,
  }
}

/**
 * 将导入层级 metadata 写回 Object3D。
 *
 * @param input 写入输入。
 */
function writeRuntimeMetadata(input: {
  objects: THREE.Object3D[]
  fileInfo: FileInfo
  node: PuppyAssemblyNode
  row: GlbHierarchyRow
  componentRootObjectId: string
  isDocumentRoot: boolean
  selectionObjectId: string
  bodyBindingSelectionTargetIndex: BodyBindingSelectionTargetIndex
}): void {
  const bodyBindingSelectionTargetsById = resolveNodeBodyBindingSelectionTargets(
    input.node,
    input.bodyBindingSelectionTargetIndex,
  )
  const shouldKeepParentBodyEntries = shouldKeepParentBodyEntriesInObjectHierarchy(input.node)
  input.objects.forEach((object) => {
    const scopedMeta = createRuntimeObjectScopedMeta(
      object,
      input.row.meta,
      shouldKeepParentBodyEntries,
    )
    const ownedBodyEntries = resolveObjectOwnedBodyEntries(object, input.row.meta)
    const scopedBodyBindingSelectionTargetsById = createScopedBodyBindingSelectionTargetsById(
      bodyBindingSelectionTargetsById,
      ownedBodyEntries,
    )
    const selectionProxyObjectId = ownedBodyEntries.length > 0
      ? object.uuid
      : input.selectionObjectId
    ImportedModelObjectMetadataService.writeMetadata(object, {
      fileId: input.fileInfo.fileId,
      hierarchy: {
        fileId: input.fileInfo.fileId,
        name: input.row.name,
        path: input.row.path,
        nodeType: input.row.nodeType,
        selectable: input.row.selectable,
        parentObjectId: input.row.parentObjectId,
        depth: input.row.depth,
        meta: scopedMeta,
      },
      selectable: input.row.selectable,
      assemblyNodeId: input.node.nodeId,
      selectionProxyObjectId,
      selectionTarget: {
        assemblyNodeId: input.node.nodeId,
      },
      // 每个非根装配 occurrence 只允许 runtime binding 的真实对象取得位姿写入权；
      // 同一 nodeId 下的 display Mesh、选择代理和拓扑宿主必须明确保持为普通后代。
      componentTransformRoot: !input.isDocumentRoot
        && object.uuid === input.componentRootObjectId,
      ...(scopedBodyBindingSelectionTargetsById
        ? { bodyBindingSelectionTargetsById: scopedBodyBindingSelectionTargetsById }
        : bodyBindingSelectionTargetsById ? { bodyBindingSelectionTargetsById } : {}),
      fileInfo: input.fileInfo,
    })
    if (typeof scopedMeta.isFixed === 'boolean') {
      AssemblyComponentFixationService.writeSceneObjectFixationState(
        object,
        scopedMeta.isFixed
          ? AssemblyComponentFixationState.Fixed
          : AssemblyComponentFixationState.Floating,
      )
    }
    //------ 装配体引用复制族 开始------
    const lineageMetadata = readReferenceLineageMetadata(input.node)
    if (lineageMetadata) {
      AssemblyReferenceCopyLineageMetadataService.writeMetadata(object, lineageMetadata)
    }
    //------ 装配体引用复制族 结束------
  })
}

/**
 * 创建装配节点索引。
 *
 * @param assembly 装配结构文档。
 * @returns 节点索引。
 */
function createAssemblyNodeIndex(assembly: PuppyAssemblyDocument): Map<string, PuppyAssemblyNode> {
  return new Map(assembly.nodes.map((node) => [node.nodeId, node]))
}

/**
 * 构建单个 hierarchy 节点。
 *
 * @param input 构建输入。
 * @returns hierarchy 节点。
 */
function buildHierarchyNode(input: {
  nodeId: string
  parentObjectId: string | null
  depth: number
  assembly: PuppyAssemblyDocument
  nodeById: Map<string, PuppyAssemblyNode>
  objectsByNodeId: Map<string, THREE.Object3D[]>
  bindingByNodeId: Map<string, PuppyAssemblyRuntimeBinding>
  root: THREE.Object3D
  fileInfo: FileInfo
  rows: GlbHierarchyRow[]
  objectIdByAssemblyNodeId: Record<string, string>
  selectionObjectIdByAssemblyNodeId: Record<string, string>
  bodyBindingSelectionTargetIndex: BodyBindingSelectionTargetIndex
}): GlbHierarchyNode {
  const node = input.nodeById.get(input.nodeId)
  if (!node) {
    throw new Error(`PuppyCAD assembly.json 引用了未知节点: ${input.nodeId}`)
  }

  const binding = input.bindingByNodeId.get(node.nodeId)
  if (!binding) {
    throw new Error(`PuppyCAD assembly.json 节点缺少运行时绑定: ${node.nodeId}`)
  }
  const runtimeObject = binding.object
  const hierarchyObjectId = binding.selectionObjectId
  const meta = createRuntimeMeta({
    node,
    binding,
  })
  const row: GlbHierarchyRow = {
    fileId: input.fileInfo.fileId,
    objectId: hierarchyObjectId,
    parentObjectId: input.parentObjectId,
    path: node.path,
    name: node.name,
    nodeType: runtimeObject.type,
    selectable: node.selectable,
    depth: input.depth,
    meta,
  }
  input.objectIdByAssemblyNodeId[node.nodeId] = runtimeObject.uuid
  input.selectionObjectIdByAssemblyNodeId[node.nodeId] = binding.selectionObjectId
  const childNodes = (input.assembly.childIdsByNodeId[node.nodeId] ?? []).map((childNodeId) => (
    buildHierarchyNode({
      ...input,
      nodeId: childNodeId,
      parentObjectId: hierarchyObjectId,
      depth: input.depth + 1,
    })
  ))
  const bodyNodes = createRuntimeBodyNodes({
    parentObjectId: hierarchyObjectId,
    parentPath: node.path,
    parentDepth: input.depth,
    parentSelectable: node.selectable,
    parentMeta: meta,
  })

  writeRuntimeMetadata({
    objects: input.objectsByNodeId.get(node.nodeId) ?? [runtimeObject],
    fileInfo: input.fileInfo,
    node,
    row,
    componentRootObjectId: runtimeObject.uuid,
    isDocumentRoot: node.nodeId === input.assembly.rootNodeId,
    selectionObjectId: binding.selectionObjectId,
    bodyBindingSelectionTargetIndex: input.bodyBindingSelectionTargetIndex,
  })
  bodyNodes.forEach((bodyNode) => {
    input.rows.push({
      fileId: input.fileInfo.fileId,
      objectId: bodyNode.objectId,
      parentObjectId: bodyNode.parentObjectId,
      path: bodyNode.path,
      name: bodyNode.name,
      nodeType: bodyNode.nodeType,
      selectable: bodyNode.selectable,
      depth: bodyNode.depth,
      meta: bodyNode.meta,
    })
  })
  input.rows.push(row)

  return {
    objectId: hierarchyObjectId,
    parentObjectId: input.parentObjectId,
    path: node.path,
    name: node.name,
    nodeType: runtimeObject.type,
    selectable: node.selectable,
    depth: input.depth,
    meta,
    children: [...childNodes, ...bodyNodes],
  }
}

/**
 * 从 `.puppy` assembly.json 恢复 imported-model 设计树。
 *
 * @param input 构建输入。
 * @returns imported-model 层级结构。
 */
export function buildPuppyAssemblyHierarchy(input: {
  /** display.glb 运行时根节点。*/
  root: THREE.Object3D
  /** `.puppy` 装配结构文档。*/
  assembly: PuppyAssemblyDocument
  /** 导入文件信息。*/
  fileInfo: FileInfo
  /** 被折叠节点到保留节点的映射，用于修正旧 display.glb 上的 assemblyNodeId。 */
  nodeIdAliasByNodeId?: Readonly<Record<string, string>>
}): PuppyAssemblyHierarchyBuildResult {
  const rows: GlbHierarchyRow[] = []
  const objectIdByAssemblyNodeId: Record<string, string> = {}
  const selectionObjectIdByAssemblyNodeId: Record<string, string> = {}
  const nodeById = createAssemblyNodeIndex(input.assembly)
  const bodyBindingSelectionTargetIndex = createBodyBindingSelectionTargetIndex(input.assembly)
  const collectedObjectsByNodeId = collectObjectsByNormalizedAssemblyNodeId(
    input.root,
    input.assembly,
    input.nodeIdAliasByNodeId ?? {},
  )
  const objectsByNodeId = new PuppyAssemblyComponentRootMaterializer().materialize({
    rootAssemblyNodeId: input.assembly.rootNodeId,
    objectsByAssemblyNodeId: collectedObjectsByNodeId,
    componentNameByAssemblyNodeId: Object.fromEntries(
      input.assembly.nodes.map((node) => [node.nodeId, node.name]),
    ),
  })
  const bindingByNodeId = createRuntimeBindingByAssemblyNodeId({
    assembly: input.assembly,
    objectsByNodeId,
    root: input.root,
  })
  const hierarchy = buildHierarchyNode({
    nodeId: input.assembly.rootNodeId,
    parentObjectId: null,
    depth: 0,
    assembly: input.assembly,
    nodeById,
    objectsByNodeId,
    bindingByNodeId,
    root: input.root,
    fileInfo: input.fileInfo,
    rows,
    objectIdByAssemblyNodeId,
    selectionObjectIdByAssemblyNodeId,
    bodyBindingSelectionTargetIndex,
  })

  return {
    hierarchy,
    rows,
    objectIdByAssemblyNodeId,
    selectionObjectIdByAssemblyNodeId,
  }
}
